/**
 * Mapa da frota.
 *
 * Marcador próprio via `divIcon`, e não o ícone padrão do Leaflet, por dois
 * motivos: o padrão depende de PNGs que se perdem no empacotamento (o clássico
 * marcador quebrado), e ele não sabe mostrar status nem rumo. Aqui a cor vem de
 * `getVehicleStatus` — a mesma função da tabela e do resumo.
 */
import { useEffect, useMemo } from "react";
import { MapContainer, Marker, TileLayer, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import { getMapLabel, getVehicleStatus, hasPosition, STATUS } from "../lib/fleet.ts";
import type { Vehicle } from "../lib/schemas.ts";
import { getTileLayer } from "../lib/tileLayers.ts";
import { useMapStore } from "../store/map.store.ts";
import { MapLayerControl } from "./MapLayerControl";
import { VehicleTrail } from "./VehicleTrail";
import { RoadLayer } from "./RoadLayer";
import { GeofenceLayer } from "./GeofenceLayer";
import { useGeofenceStore } from "../store/geofence.store.ts";
import type { TelemetryRow } from "../lib/schemas.ts";
import type { TrailPoint } from "../lib/trail.ts";

/** Centro inicial quando ainda não há posição: São Paulo. */
const FALLBACK_CENTER: [number, number] = [-23.5505, -46.6333];

function buildIcon(vehicle: Vehicle, darkBasemap: boolean): L.DivIcon {
  const status = getVehicleStatus(vehicle);
  const { color } = STATUS[status];
  const heading = vehicle.heading ?? 0;
  // O pulso só em quem se move: animar tudo faz o mapa inteiro tremer e o olho
  // perde justamente o que deveria saltar.
  const pulse = status === "moving" ? `<span class="marker-pulse absolute inset-0 rounded-full" style="background:${color}"></span>` : "";
  // A seta só aparece quando há rumo — desenhar 0° como "norte" seria inventar.
  const arrow =
    vehicle.heading == null
      ? ""
      : `<span class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" style="transform:rotate(${heading}deg) translateY(-11px)">
           <svg width="8" height="8" viewBox="0 0 8 8"><path d="M4 0 L8 8 L4 6 L0 8 Z" fill="${color}"/></svg>
         </span>`;

  // O anel escuro some sobre satélite — que é imagem escura e ruidosa. Sobre
  // base clara, um anel branco apagaria o marcador. A borda acompanha a camada.
  const ring = darkBasemap ? "ring-white/80" : "ring-slate-950";

  return L.divIcon({
    className: "",
    iconSize: [18, 18],
    iconAnchor: [9, 9],
    html: `<span class="relative flex size-[18px] items-center justify-center">
        ${pulse}
        <span class="relative block size-2.5 rounded-full ring-2 ${ring}" style="background:${color}"></span>
        ${arrow}
      </span>`,
  });
}

/**
 * Expõe a instância do Leaflet para o teste de fumaça inspecionar a camada ativa.
 * Só isso: a aplicação não lê esta variável em lugar nenhum.
 */
function ExposeMapForTests() {
  const map = useMap();
  useEffect(() => {
    (window as unknown as { __mtMap?: L.Map }).__mtMap = map;
  }, [map]);
  return null;
}

/** Centraliza no veículo escolhido, sem brigar com o zoom de quem está olhando. */
function FocusOnSelection({ vehicle }: { vehicle: Vehicle | null }) {
  const map = useMap();
  useEffect(() => {
    if (vehicle && hasPosition(vehicle)) {
      map.flyTo([vehicle.lat, vehicle.lon], Math.max(map.getZoom(), 15), { duration: 0.8 });
    }
  }, [vehicle, map]);
  return null;
}

/** Enquadra a frota inteira na primeira carga. Depois, não mexe mais. */
function FitOnFirstLoad({ vehicles }: { vehicles: Vehicle[] }) {
  const map = useMap();
  const positioned = vehicles.filter(hasPosition);
  const ready = positioned.length > 0;
  useEffect(() => {
    if (!ready) return;
    map.fitBounds(L.latLngBounds(positioned.map((v) => [v.lat, v.lon] as [number, number])), {
      padding: [48, 48],
      maxZoom: 15,
    });
    // Só na primeira vez: reenquadrar a cada atualização arrancaria o mapa das
    // mãos de quem está examinando um ponto. Por isso a dependência é `ready`
    // (booleano que vira true uma vez) e não a lista, que muda a cada delta.
  }, [ready]);
  return null;
}

export function FleetMap({
  vehicles,
  selected,
  selectedTrail,
  selectedHistory,
  onSelect,
}: {
  vehicles: Vehicle[];
  selected: Vehicle | null;
  selectedTrail: readonly TrailPoint[];
  selectedHistory: readonly TelemetryRow[];
  onSelect: (imei: string) => void;
}) {
  const positioned = useMemo(() => vehicles.filter(hasPosition), [vehicles]);
  const tileLayerId = useMapStore((state) => state.tileLayerId);
  const showRoadLayer = useMapStore((state) => state.showRoadLayer);
  const showGeofences = useMapStore((state) => state.showGeofences);
  const geofences = useGeofenceStore((state) => state.items);
  const basemap = getTileLayer(tileLayerId);

  return (
    <MapContainer
      center={FALLBACK_CENTER}
      zoom={12}
      className="size-full"
      zoomControl={false}
      preferCanvas
    >
      {/* `key` obriga a recriar a camada ao trocar de base.
          O react-leaflet ATUALIZA a `url` sozinho — isso foi medido, e sem o
          `key` os ladrilhos novos carregam normalmente. O que ele NÃO atualiza é
          o `maxZoom`: trocando Carto (19) por Google (20), o mapa continuaria
          travado em 19. Sintoma péssimo de diagnosticar, porque aparece só como
          "não dá para aproximar mais" e ninguém liga isso à troca de camada.
          Há teste, e ele reprova quando o `key` sai. */}
      <TileLayer
        key={basemap.id}
        url={basemap.url}
        attribution={basemap.attribution}
        maxZoom={basemap.maxZoom}
      />
      <ExposeMapForTests />

      {/* Antes dos marcadores: assim a linha passa POR BAIXO deles, e o veículo
          continua sendo a coisa mais visível do mapa. */}
      {/* Antes do rastro e dos marcadores: a cerca é o fundo sobre o qual se
          lê a posição, não o objeto em foco. */}
      {showGeofences ? <GeofenceLayer geofences={geofences} /> : null}
      {selected ? <VehicleTrail trail={selectedTrail} /> : null}
      {selected && showRoadLayer ? <RoadLayer rows={selectedHistory} /> : null}
      <FitOnFirstLoad vehicles={vehicles} />
      <FocusOnSelection vehicle={selected} />

      {positioned.map((vehicle) => (
        <Marker
          key={vehicle.imei}
          position={[vehicle.lat, vehicle.lon]}
          icon={buildIcon(vehicle, basemap.dark)}
          eventHandlers={{ click: () => onSelect(vehicle.imei) }}
        >
          {/* Etiqueta FIXA, só com o nome.
              `permanent`: era um tooltip de hover, e hover não existe em tela
              de toque nem ajuda quem precisa achar um veículo entre vários —
              para ler o nome era preciso adivinhar qual ponto apontar.
              Só o nome, e a velocidade saiu daqui: ela muda a cada delta, e
              texto que se reescreve sozinho sobre o mapa rouba o olho do que
              está sendo examinado. Ela continua na lista e no detalhe.
              Sem nome de veículo, entra a BASE e não o IMEI (`getMapLabel`):
              quinze dígitos sobre o mapa não dizem de quem é aquele ponto. */}
          <Tooltip permanent direction="top" offset={[0, -12]} opacity={1} className="vehicle-label">
            {getMapLabel(vehicle)}
          </Tooltip>
        </Marker>
      ))}

      <MapLayerControl />
    </MapContainer>
  );
}
